import {
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Text,
  VStack,
  HStack,
  Badge,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
  Spinner,
  useColorModeValue,
  chakra,
} from "@chakra-ui/react";
import { useState, useEffect, useRef, FormEvent, useLayoutEffect } from "react";

import { $ok } from "foundation-ts/commons";
import { Nullable } from "foundation-ts/types";

import { config } from "@/config/config";

interface MollieCardFormProps {
    profileId: string;
    planLabel: string;
    testMode: boolean;
    onSuccess?: (token: string) => Promise<void>;
}

export default function MollieCardForm(props : MollieCardFormProps) {
    const { profileId, planLabel, testMode, onSuccess } = props;
    const [mollie, setMollie] = useState<Nullable<MollieInstance>>(null);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        cardHolder: null,
        cardNumber: null,
        expiryDate: null,
        verificationCode: null,
    });
    const [formError, setFormError] = useState<Nullable<string>>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [mollieReady, setMollieReady] = useState<boolean>(false);
    const mountedRef = useRef<boolean>(false);
    const componentsRef = useRef<Partial<Record<FieldKey, MollieComponent>>>({});

    const borderColor = useColorModeValue("gray.200", "gray.600");
    const fieldBg = "transparent"; // useColorModeValue("gray.50", "gray.700");
    const subtleBg = useColorModeValue("blue.50", "blue.900");
    const subtleText = useColorModeValue("blue.700", "blue.200");
    const mollieStyles = useColorModeValue(
        {
            base: {
                color: "#E2E8F0", // "#1A202C",
                fontSize: "14px",
                lineHeight: "38px",
                "::placeholder": { color: "#4A5568"/*"#A0AEC0"*/ },
            },
            valid:   { color:"#E2E8F0" },
            invalid: { color: "#E53E3E" },
        },
        {
            base: {
                color: "#E2E8F0",
                fontSize: "14px",
                lineHeight: "38px",
                "::placeholder": { color: "#4A5568" },
            },
            valid:   { color: "#E2E8F0" },
            invalid: { color: "#FC8181" },
        }
    );

    function initMollie(): void {
        if (mountedRef.current) { return; }
        mountedRef.current = true;

        const m = window.Mollie(profileId, { locale: "fr_FR", testmode: testMode });

        const componentOptions = { styles: mollieStyles };
        const cardHolder       = m.createComponent("cardHolder",       componentOptions);
        const cardNumber       = m.createComponent("cardNumber",       componentOptions);
        const expiryDate       = m.createComponent("expiryDate",       componentOptions);
        const verificationCode = m.createComponent("verificationCode", componentOptions);
 
        const mountMap: Array<{ component: MollieComponent; selector: string; key: FieldKey }> = [
            { component: cardHolder,      selector: "#mollie-card-holder",        key: "cardHolder" },
            { component: cardNumber,      selector: "#mollie-card-number",        key: "cardNumber" },
            { component: expiryDate,      selector: "#mollie-expiry-date",        key: "expiryDate" },
            { component: verificationCode,selector: "#mollie-verification-code",  key: "verificationCode" },
        ];
 
        const mounted: Partial<Record<FieldKey, MollieComponent>> = {};
        mountMap.forEach(({ component, selector, key }) => {
            if (document.querySelector(selector)) {
                component.mount(selector);
                mounted[key] = component;
                component.addEventListener("change", (event: MollieComponentEvent) => {
                    setFieldErrors((prev) => ({
                        ...prev,
                        [key]: event.error ? event.error.message : null,
                    }));
                });
            }
        })

        componentsRef.current = mounted;
        setMollie(m);
        setMollieReady(true);
    }

    async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
        e.preventDefault();
        if (!$ok(mollie)) { return; }

        setFormError(null);
        setLoading(true);

        try {
            const { token, error } = await mollie!.createToken();
            if (error || !token) {
                setFormError(error?.message ?? "Erreur lors de la tokenisation.");
                return;
            }

            await onSuccess?.(token);
        } catch {
            setFormError("Une erreur inattendue est survenue.");
        } finally {
            setLoading(false);
        }
    }

    const mollieField = (id: string, errorKey: FieldKey, showSpinner: boolean = false) => (
        <>
            {showSpinner && !mollieReady && (
                <HStack h="40px" px={3} border="1px solid" borderColor={borderColor} borderRadius="md" bg={fieldBg}>
                    <Spinner size="xs" color="gray.400" />
                    <Text fontSize="xs" color="gray.400">Chargement sécurisé…</Text>
                </HStack>
            )}
            <Box id={id} sx={mollieReady ? {} : { display: "none" }} h="40px" px={3} border="1px solid" borderColor={fieldErrors[errorKey] ? "red.400" : borderColor} borderRadius="md" bg={fieldBg} />
        </>
    );

    useLayoutEffect(() => {
        return () => {
            Object.values(componentsRef.current).forEach((c) => {
                try {
                    c?.unmount();
                } catch {
                }
            });
            componentsRef.current = {};
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        let scriptRef: HTMLScriptElement | null = null;

        function safeInitMollie(): void {
            if (cancelled) { return; }
            initMollie();
            setMollieReady(true);
        }

        const existingScript = document.querySelector<HTMLScriptElement>(
            `script[src="${config.app.mollie.scriptURL}"]`
        );
 
        if ($ok(existingScript)) {
            if (existingScript!.dataset.loaded === "true") {
                safeInitMollie();
            } else {
                existingScript!.addEventListener("load", safeInitMollie, { once: true });
            }
        } else {
            const script = document.createElement("script");
            script.src = config.app.mollie.scriptURL!;
            script.addEventListener("load", () => {
                script.dataset.loaded = "true";
                safeInitMollie();
            }, { once: true });
            document.head.appendChild(script);
            scriptRef = script;
        }

        return () => {
            cancelled = true;
            mountedRef.current = false;
            if (scriptRef && scriptRef.dataset.loaded !== "true") {
                scriptRef.removeEventListener("load", safeInitMollie);
            }
        };
    }, []);

    return (
        <chakra.form p={6} borderRadius="xl" border="1px solid" borderColor={borderColor} bg="prussianBlue.500" onSubmit={handleSubmit}>
            <HStack justify="space-between" mb={5}>
                <VStack align="start" spacing={0}>
                    <Text fontWeight="600" fontSize="lg">Paiement par carte</Text>
                    <Text fontSize="sm" color="gray.500">{planLabel}</Text>
                </VStack>
                <Badge colorScheme="green" fontSize="xs" px={2} py={1} borderRadius="md">
                    First payment
                </Badge>
            </HStack>

            <Box bg={subtleBg} color={subtleText} fontSize="xs" px={3} py={2} borderRadius="md" mb={5}>
                Ce premier paiement active votre abonnement. Les suivants seront prélevés automatiquement
                via le mandat enregistré.
            </Box>

            <VStack spacing={4}>
                <FormControl isRequired>
                    <FormLabel fontSize="sm">Titulaire de la carte</FormLabel>
                    { mollieField("mollie-card-holder", "cardHolder", true) }
                </FormControl>

                <FormControl isInvalid={!!fieldErrors.cardNumber}>
                    <FormLabel fontSize="sm">Numéro de carte</FormLabel>
                    { mollieField("mollie-card-number", "cardNumber") }
                    <FormErrorMessage>{fieldErrors.cardNumber}</FormErrorMessage>
                </FormControl>

                <HStack w="full" spacing={4}>
                    <FormControl isInvalid={!!fieldErrors.expiryDate}>
                        <FormLabel fontSize="sm">Expiration</FormLabel>
                        { mollieField("mollie-expiry-date", "expiryDate") }
                        <FormErrorMessage>{fieldErrors.expiryDate}</FormErrorMessage>
                    </FormControl>

                    <FormControl isInvalid={!!fieldErrors.verificationCode}>
                    <FormLabel fontSize="sm">CVV</FormLabel>
                        { mollieField("mollie-verification-code", "verificationCode") }
                    </FormControl>
                </HStack>
            </VStack>

            {formError && (
                <Alert status="error" borderRadius="md" mt={4} fontSize="sm">
                    <AlertIcon />
                    <AlertDescription>{formError}</AlertDescription>
                </Alert>
            )}

            <Divider my={5} />

            <Button type="submit" colorScheme="blue" w="full" size="md" isLoading={loading} loadingText="Traitement en cours…" isDisabled={!mollieReady}>
                Activer l'abonnement
            </Button>

            <Text fontSize="xs" color="gray.400" textAlign="center" mt={3}>Paiement sécurisé par Mollie</Text>
        </chakra.form>
    );
}

type FieldKey = "cardHolder" | "cardNumber" | "expiryDate" | "verificationCode";
type FieldErrors = Record<FieldKey, string | null>;

interface MollieComponent {
    mount: (selector: string) => void;
    unmount: () => void;
    addEventListener: (event: "change", handler: (e: MollieComponentEvent) => void) => void;
}

interface MollieComponentEvent {
    error?: { message: string };
}

interface MollieTokenResult {
    token?: string;
    error?: { message: string };
}

interface MollieInstance {
    createComponent: (type: FieldKey, options?: object) => MollieComponent;
    createToken: () => Promise<MollieTokenResult>;
}

declare global {
    interface Window {
        Mollie: (profileId: string, options: { locale: string; testmode: boolean }) => MollieInstance;
    }
}